import React, { useEffect, useState, useCallback } from 'react';
import api from '../api/client';
import { useAuth, hasRole } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { PageHeader } from '../components/PageHeader';
import { StatusBadge } from '../components/StatusBadge';
import { Trash2, Eye, FileText } from 'lucide-react';
import { formatDate } from '../lib/format';
import { useToast } from '../hooks/use-toast';
import { EquipmentCell } from '../components/EquipmentCell';
import { useNavigate } from 'react-router-dom';

const Mobilizations = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [operators, setOperators] = useState([]);
  const [editingDates, setEditingDates] = useState({}); // { [id]: { scheduled_departure, scheduled_arrival } }
  const [detail, setDetail] = useState(null);
  const [detailForm, setDetailForm] = useState({});
  const canDelete = hasRole(user, 'super_admin','admin');

  const load = useCallback(async () => {
    const r = await api.get('/mobilizations'); setItems(r.data);
    const v = await api.get('/vehicles'); setVehicles(v.data);
    const ops = await api.get('/mobilizations/available-operators'); setOperators(ops.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const vehById = (id) => vehicles.find((v) => v.id === id) || {};
  const opById = (id) => operators.find((o) => o.id === id) || {};

  const updateField = async (id, fields) => {
    try {
      await api.put(`/mobilizations/${id}`, fields);
      toast({ title: 'Updated' });
      load();
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail, variant: 'destructive' }); }
  };
  const onDateChange = (id, key, value) => setEditingDates((p) => ({ ...p, [id]: { ...p[id], [key]: value } }));
  const flushDate = (id, key) => {
    const val = editingDates[id]?.[key];
    if (val !== undefined) updateField(id, { [key]: val });
  };
  const remove = async (id) => {
    if (!window.confirm('Delete mobilization?')) return;
    await api.delete(`/mobilizations/${id}`); toast({ title: 'Deleted' }); load();
  };
  const openDetail = async (m) => { setDetail(m); setDetailForm({ ...m }); };
  const saveDetail = async () => {
    try {
      await api.put(`/mobilizations/${detail.id}`, detailForm);
      setDetail(null); load();
      toast({ title: 'Updated' });
    } catch (e) {
      toast({ title: 'Update failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Mobilizations" description="Track equipment deployments. Auto-generated from approved inspections." />
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b text-left text-slate-600">
              <tr>
                <th className="p-3">Ref #</th><th className="p-3">Equipment</th><th className="p-3">Operator</th>
                <th className="p-3">Sched. Departure</th><th className="p-3">Sched. Arrival</th>
                <th className="p-3">Destination</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {items.length === 0 ? <tr><td colSpan={8} className="p-8 text-center text-slate-500">No mobilizations yet.</td></tr> :
                items.map((m) => {
                  const v = vehById(m.vehicle_id);
                  const sigs = m.signatures || {};
                  const hasHandover = !!sigs.receiver;
                  const isLocked = m.status === 'completed' && hasHandover;
                  const editable = ['pending','in_progress'].includes(m.status) && !isLocked;
                  const dateVal = (k) => editingDates[m.id]?.[k] ?? (m[k] ? m[k].slice(0,10) : '');
                  return (
                    <tr key={m.id} className="table-row-hover">
                      <td className="p-3 font-mono text-xs">{m.reference_number}</td>
                      <td className="p-3"><EquipmentCell vehicle={v} /></td>
                      <td className="p-3">
                        {editable ? (
                          <Select value={m.operator_id || 'none'} onValueChange={(val) => updateField(m.id, { operator_id: val === 'none' ? null : val })}>
                            <SelectTrigger className="h-8 w-48"><SelectValue placeholder="Assign operator..." /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">— Unassigned —</SelectItem>
                              {operators.map((o) => <SelectItem key={o.id} value={o.id}>{o.full_name}</SelectItem>)}
                              {m.operator_id && !operators.find((o) => o.id === m.operator_id) && <SelectItem value={m.operator_id}>(current)</SelectItem>}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span>{opById(m.operator_id).full_name || '—'}</span>
                        )}
                      </td>
                      <td className="p-3">
                        {editable ? <Input type="date" className="h-8 w-36" value={dateVal('scheduled_departure')} onChange={(e) => onDateChange(m.id, 'scheduled_departure', e.target.value)} onBlur={() => flushDate(m.id, 'scheduled_departure')} /> : (m.scheduled_departure ? formatDate(m.scheduled_departure) : '—')}
                      </td>
                      <td className="p-3">
                        {editable ? <Input type="date" className="h-8 w-36" value={dateVal('scheduled_arrival')} onChange={(e) => onDateChange(m.id, 'scheduled_arrival', e.target.value)} onBlur={() => flushDate(m.id, 'scheduled_arrival')} /> : (m.scheduled_arrival ? formatDate(m.scheduled_arrival) : '—')}
                      </td>
                      <td className="p-3 text-xs">{m.destination || '—'}</td>
                      <td className="p-3">
                        {editable ? (
                          <Select value={m.status} onValueChange={(val) => updateField(m.id, { status: val })}>
                            <SelectTrigger className="h-8 w-32" data-testid={`mob-status-select-${m.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="completed" data-testid={`mob-status-completed-option-${m.id}`}>Completed</SelectItem>
                              <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <StatusBadge status={m.status} type="request" />
                            {isLocked && (
                              <span
                                title="Locked — Acknowledgement & Handover has been signed by the receiver."
                                className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 font-medium"
                                data-testid={`mob-status-locked-${m.id}`}
                              >LOCKED</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="p-3 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/mobilizations/${m.id}/form`)} title="Mobilization Form" data-testid={`mob-form-btn-${m.id}`}>
                          <FileText className="h-4 w-4 text-[hsl(217,91%,24%)]" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openDetail(m)}><Eye className="h-4 w-4" /></Button>
                        {canDelete && <Button size="sm" variant="ghost" onClick={() => remove(m.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Mobilization Details</DialogTitle><DialogDescription>{detail?.reference_number}</DialogDescription></DialogHeader>
          {detail && (() => {
            const detailLocked = detail.status === 'completed' && !!(detail.signatures?.receiver);
            return (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Destination</Label><Input value={detailForm.destination || ''} onChange={(e) => setDetailForm({ ...detailForm, destination: e.target.value })} /></div>
                <div>
                  <Label>Status</Label>
                  <Select
                    value={detailForm.status}
                    onValueChange={(v) => setDetailForm({ ...detailForm, status: v })}
                    disabled={detailLocked}
                  >
                    <SelectTrigger data-testid="mob-detail-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                  {detailLocked && (
                    <div className="mt-1 text-[11px] text-emerald-700" data-testid="mob-detail-locked-hint">Locked — receiver has signed the Acknowledgement & Handover.</div>
                  )}
                </div>
                <div><Label>Actual Departure</Label><Input type="date" value={(detailForm.actual_departure || '').slice(0,10)} onChange={(e) => setDetailForm({ ...detailForm, actual_departure: e.target.value })} /></div>
                <div><Label>Actual Arrival</Label><Input type="date" value={(detailForm.actual_arrival || '').slice(0,10)} onChange={(e) => setDetailForm({ ...detailForm, actual_arrival: e.target.value })} /></div>
                <div className="col-span-2"><Label>Remarks</Label><Textarea rows={2} value={detailForm.remarks || ''} onChange={(e) => setDetailForm({ ...detailForm, remarks: e.target.value })} /></div>
              </div>
            );
          })()}
          <DialogFooter><Button variant="outline" onClick={() => setDetail(null)}>Cancel</Button><Button onClick={saveDetail} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]" data-testid="mob-detail-save-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Mobilizations;
